import type { ChargerFlowEdges, ChargerFlowNodes } from '@models/chargerFlows';
import type { DefaultNodeData, NodeItem } from '@models/flows';
import { NodeTypes } from './node-types';
import type { Node } from '@xyflow/react';
import { isNodeType } from './validation-helpers';

export const validateNodesBeforeSave = (
  flowNodes: ChargerFlowNodes,
  flowEdges: ChargerFlowEdges,
): string | null => {
  for (const node of flowNodes) {
    const nodeType = node.type as string;
    const data = node.data as unknown as DefaultNodeData;
    const items = data?.items ?? [];

    switch (nodeType) {
      case NodeTypes.conditionalNode: {
        const err = validateConditionalNodeOnSave(node.id, items, flowNodes, flowEdges);
        if (err) return err;
        break;
      }
      case NodeTypes.testGroupsNode: {
        const err = validateTestGroupsNodeOnSave(items);
        if (err) return err;
        break;
      }
      case NodeTypes.settingsNode: {
        const err = validateSettingsNodeOnSave(items);
        if (err) return err;
        break;
      }
      case NodeTypes.valueNode: {
        if (isScalarNodeValueEmpty(items[0]?.value)) {
          return 'Settings Value Node: enter a value.';
        }
        break;
      }
      case NodeTypes.debtCollectionNode: {
        const debtItem = items[0];
        if (debtItem?.code !== 'debt_flow' || debtItem?.value !== true) {
          return 'Debt Collection Node: invalid configuration.';
        }
        break;
      }
      case NodeTypes.failureNode: {
        if (isScalarNodeValueEmpty(items[0]?.value)) {
          return 'Failure Node: enter a decrease percentage.';
        }
        break;
      }
      case NodeTypes.successNode: {
        const err = validateSuccessNodeOnSave(items);
        if (err) return err;
        break;
      }
      default:
        break;
    }
  }

  return null;
};

function validateTestGroupsNodeOnSave(items: NodeItem[]): string | null {
  if (items.length === 0) {
    return 'Test Groups Node: add at least one test group.';
  }
  if (!items.every(isTestGroupItemConfigured)) {
    return 'Test Groups Node: configure each row with at least one value (or select All).';
  }
  return null;
}

function validateSuccessNodeOnSave(items: NodeItem[]): string | null {
  const item = items[0];
  const ri = item?.retryInterval;
  const hasInterval = (ri?.days ?? 0) > 0 || (ri?.hours ?? 0) > 0 || (ri?.minutes ?? 0) > 0;
  if (!hasInterval) {
    return 'Success Node: set Recharge every (days, hours, or minutes).';
  }
  return null;
}

function validateSettingsNodeOnSave(items: NodeItem[]): string | null {
  const allHaveSetting = items.every(
    (item) => item.settingId && String(item.settingId).trim() !== '',
  );
  if (!allHaveSetting) {
    return 'Settings Node: select a setting for each row.';
  }
  return null;
}
const isTestGroupItemConfigured = (item: NodeItem): boolean => {
  if (item.isAllSelected === true) return true;
  const value = item.value;
  if (!Array.isArray(value) || value.length === 0) return false;
  return value.some(isNonEmptyTestGroupValue);
};

const isScalarNodeValueEmpty = (v: unknown): boolean =>
  v === '' || v === null || v === undefined || (typeof v === 'number' && Number.isNaN(v));

function validateConditionalNodeOnSave(
  nodeId: string,
  items: NodeItem[],
  flowNodes: ChargerFlowNodes,
  flowEdges: ChargerFlowEdges,
): string | null {
  const item = items[0];
  if (!item?.code?.trim()) {
    return 'Conditional Node: select a condition type.';
  }
  if (item.code !== 'everythingElse') {
    const valueArr = item.value;
    if (!Array.isArray(valueArr) || !valueArr.some((v) => String(v ?? '').trim() !== '')) {
      return 'Conditional Node: add at least one value.';
    }
  }

  const outgoing = flowEdges.filter((e) => e.source === nodeId);
  if (outgoing.length === 1) {
    const targetId = outgoing[0]?.target;
    const targetNode = flowNodes.find((n) => n.id === targetId);
    const targetType = (targetNode?.type as string) || '';
    if (
      !isNodeType(
        targetType,
        NodeTypes.testGroupsNode,
        NodeTypes.valueNode,
        NodeTypes.recurringActionNode,
      )
    ) {
      return 'Conditional Node: must connect to Test Groups , Recurring Action Node or Settings Value Node.';
    }
  }

  const outgoingTargets = outgoing
    .map((e) => flowNodes.find((n) => n.id === e.target))
    .filter((n): n is Node => Boolean(n));
  const valueTargets = outgoingTargets.filter((n) =>
    isNodeType(n.type as string, NodeTypes.valueNode),
  );
  const recurringTargets = outgoingTargets.filter((n) =>
    isNodeType(n.type as string, NodeTypes.recurringActionNode),
  );
  if (valueTargets.length > 0 && recurringTargets.length > 0) {
    return 'Conditional Node cannot connect to both a Recurring Action Node and a Settings Value Node.';
  }
  if (valueTargets.length > 1) {
    return 'Conditional Node can be connected to only one Settings Value Node.';
  }
  if (recurringTargets.length > 1) {
    return 'Conditional Node can only connect to one Recurring Action Node.';
  }
  return null;
}
const isNonEmptyTestGroupValue = (v: unknown): boolean => {
  if (v === '' || v == null) return false;
  if (typeof v === 'string') return v.trim() !== '';
  return true;
};
